// src/receptionist/components/BookingDrawer.tsx
//
// A booking, read the way the candidate profile is read: a right-hand drawer
// over the list, not a row that pushes everything below it down the page.
// Same shell as containers/drawer/drawer-candidate-profile.tsx — a scrim, a
// fixed panel that slides in from the right, a header with the close button,
// and a scrolling body with the actions pinned to the foot.

import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangleIcon, Loader2Icon, SendIcon, XIcon, ZapIcon } from "lucide-react";
import { Button } from "./kit";
import { Textarea } from "@component/textarea";
import { sendBookingMessage } from "@service/receptionist";
import { FollowUpDialog } from "./BookingDialogs";
import { cn } from "@oura/lib/cn";
import { isImage, isReplyWindowOpen, MEDIA_PLACEHOLDER, slotWhen, type Booking, type Turn } from "../bookings";

const STATUS_STYLE: Record<Booking["status"], string> = {
    Pending: "bg-amber-100 text-amber-700",
    Confirmed: "bg-emerald-100 text-emerald-700",
    Declined: "bg-gray-200 text-gray-600",
    Cancelled: "bg-gray-200 text-gray-600",
};

function TranscriptTurn({ turn }: { turn: Turn }) {
    if (turn.who === "system") {
        return (
            <div className="self-center rounded-md bg-amber-50 px-3 py-1 text-center text-xs text-amber-700">
                {turn.text}
            </div>
        );
    }
    const fromCustomer = turn.who === "customer";
    const media = turn.media ?? [];
    // A turn that was only an attachment is logged as "[media]": with the attachment here
    // to show, the placeholder says nothing (ourteam-frontend#4899).
    const text = media.length && turn.text.trim() === MEDIA_PLACEHOLDER ? "" : turn.text;
    return (
        <div
            className={cn(
                "max-w-[84%] whitespace-pre-line rounded-lg px-3 py-2 text-sm leading-snug shadow-sm",
                fromCustomer ? "self-start bg-white text-gray-900" : "self-end bg-blue-100 text-gray-900",
            )}
        >
            {media.length > 0 && (
                <div className={cn("flex flex-wrap gap-2", text && "mb-2")}>
                    {media.map((m, i) =>
                        isImage(m) ? (
                            <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="block">
                                <img
                                    src={m.url}
                                    alt={fromCustomer ? "Photo from the customer" : "Picture sent to the customer"}
                                    loading="lazy"
                                    className="max-h-[220px] max-w-[220px] rounded-md object-cover"
                                />
                            </a>
                        ) : (
                            <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
                               className="text-blue-600 underline underline-offset-2">
                                Open attachment
                            </a>
                        ),
                    )}
                </div>
            )}
            {text}
        </div>
    );
}

/**
 * A reply on the customer's own thread, sized to WhatsApp's rule rather than
 * ours: outside the 24-hour window nothing free-form is delivered, so the box
 * stays visible but disabled with the reason said out loud — the same shape as
 * the candidate composer (containers/drawer/tabs/whatsapp-reply-composer.tsx).
 * The server's refusal is still the authority; this is only the early warning.
 */
function Composer({
    booking,
    onSent,
    trailingAction,
}: {
    booking: Booking;
    onSent: (b: Booking) => void;
    /** Rendered beside Send, and beside the blocked banner too. */
    trailingAction?: ReactNode;
}) {
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [blocked, setBlocked] = useState<string | null>(null);

    // A different booking is a different thread; nothing carries over.
    useEffect(() => { setDraft(""); setError(null); setBlocked(null); }, [booking.id]);

    const windowOpen = isReplyWindowOpen(booking);
    const disabled = sending || !windowOpen || blocked !== null;

    const send = async () => {
        const text = draft.trim();
        if (!text || disabled) return;
        setSending(true);
        setError(null);
        try {
            const result = await sendBookingMessage(booking.id, { text });
            if (result.ok) { setDraft(""); onSent(result.booking); return; }
            if (result.kind === "window_closed") { setBlocked(result.message); return; }
            setError(result.message);
        } finally {
            setSending(false);
        }
    };

    if (blocked) {
        return (
            <div className="flex items-start gap-2">
                <div className="flex flex-1 items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-2">
                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-700">{blocked}</p>
                </div>
                {trailingAction}
            </div>
        );
    }

    return (
        <div>
            {!windowOpen && (
                <p className="mb-1.5 text-[11px] text-gray-400">
                    You can't reply right now — WhatsApp only delivers replies within 24 hours of the
                    customer's last message.
                </p>
            )}
            {error && <p className="mb-1.5 text-xs text-rose-600">{error}</p>}
            <div className="flex items-end gap-2">
                <Textarea
                    rows={1}
                    value={draft}
                    disabled={disabled}
                    placeholder="Reply to the customer on WhatsApp…"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
                    }}
                    className="max-h-32 min-h-0 flex-1 resize-none rounded-lg border-gray-300 bg-white px-3 py-2 text-sm shadow-none placeholder:text-gray-400 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900"
                />
                <Button
                    size="icon"
                    onClick={() => void send()}
                    disabled={!draft.trim() || disabled}
                    aria-label="Send reply"
                    title={windowOpen ? "Send reply" : "The 24-hour reply window has closed"}
                    className="flex-shrink-0"
                >
                    {sending ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
                </Button>
                {trailingAction}
            </div>
        </div>
    );
}

export function BookingDrawer({
    booking,
    onClose,
    onDecline,
    onStale,
    onSent,
    merchantId,
    profileHref,
}: {
    /** null keeps the panel mounted but off-screen, so it slides rather than pops. */
    booking: Booking | null;
    onClose: () => void;
    /** The server's booking after a decline lands, so the row and thread redraw. */
    onDecline: (booking: Booking) => void;
    /** The staff group decided first; the list needs refetching. */
    onStale: (message: string) => void;
    /** Whose template pack the follow-up dialog offers. */
    merchantId: string | null;
    /** The server's booking after a reply lands, so the thread redraws. */
    onSent: (booking: Booking) => void;
    profileHref: string;
}) {
    const open = booking !== null;

    // Escape closes it, and the page behind does not scroll while it is open —
    // both are what the candidate drawer does.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    const [followUpOpen, setFollowUpOpen] = useState(false);

    // A different booking is a different decision; the dialog does not carry over.
    useEffect(() => { setFollowUpOpen(false); }, [booking?.id]);

    return (
        <>
            <div
                onClick={onClose}
                aria-hidden
                className={cn(
                    "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300",
                    open ? "opacity-100" : "pointer-events-none opacity-0",
                )}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={booking ? `Booking for ${booking.customer}` : "Booking"}
                className={cn(
                    "fixed inset-y-0 right-0 z-50 flex w-full transform flex-col border-l bg-white transition-transform duration-300 ease-in-out sm:w-[600px] md:w-[700px] lg:w-1/2 dark:bg-gray-900",
                    open ? "translate-x-0" : "translate-x-full",
                )}
            >
                {booking && (
                    <>
                        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-800">
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">{booking.customer}</h2>
                                <p className="text-sm text-gray-500">{booking.phone}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="h-8 w-8 flex-shrink-0">
                                <XIcon className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS_STYLE[booking.status] ?? "bg-gray-200 text-gray-600")}>
                                    {booking.status}
                                </span>
                                {(booking.tags ?? []).map((t) => (
                                    <span key={t} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">{t}</span>
                                ))}
                            </div>
                            <dl className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                    <dt className="text-[13px] leading-[18px] font-medium text-gray-500">Service</dt>
                                    <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{booking.service}</dd>
                                </div>
                                <div>
                                    {/* The stored moment, then the customer's own words (#4798). */}
                                    <dt className="text-[13px] leading-[18px] font-medium text-gray-500">Slot</dt>
                                    <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                                        {(() => {
                                            const when = slotWhen(booking);
                                            return when ? `${when.date}, ${when.time}` : booking.slot || "—";
                                        })()}
                                    </dd>
                                    {booking.slot && (
                                        <dd className="mt-0.5 text-xs text-gray-500">
                                            {slotWhen(booking) ? `Customer said: ${booking.slot}` : "No date yet"}
                                        </dd>
                                    )}
                                </div>
                            </dl>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-950">
                            {/* `source` is rendered as the server sends it — three known
                                values today, and a newer one still reads. */}
                            <p className="mb-2 text-[13px] leading-[18px] font-medium text-gray-500">
                                Conversation · {booking.source}
                            </p>
                            {/* The transcript arrived with the row; no second call.
                                A very long thread is the server's last 100 turns. */}
                            <div className="flex flex-col gap-1.5">
                                {(booking.transcript ?? []).map((t, i) => <TranscriptTurn key={i} turn={t} />)}
                            </div>
                            {booking.status !== "Pending" && booking.note && (
                                <p className="mt-3 text-xs text-gray-500">{booking.note}</p>
                            )}
                        </div>

                        <div className="border-t border-gray-200 p-4 dark:border-gray-800">
                            <Composer
                                booking={booking}
                                onSent={onSent}
                                trailingAction={
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-10 flex-shrink-0 gap-1.5 text-xs"
                                        onClick={() => setFollowUpOpen(true)}
                                    >
                                        <ZapIcon className="h-3.5 w-3.5" />
                                        Follow up
                                    </Button>
                                }
                            />
                        </div>
                        <FollowUpDialog
                            open={followUpOpen}
                            onOpenChange={setFollowUpOpen}
                            booking={booking}
                            merchantId={merchantId}
                            profileHref={profileHref}
                            onSent={onSent}
                            onDeclined={onDecline}
                            onStale={onStale}
                        />
                    </>
                )}
            </div>
        </>
    );
}
